/**
 * Minimal pub/sub toast emitter, callable from plain JS (services, error handler) as well as
 * React code. A <ToastHost/> subscribes and renders the actual MUI Snackbar/Alert.
 *
 * A toast fired before any host mounts is buffered and flushed to the first subscriber, so
 * early failures (e.g. during app bootstrap) are not silently lost. Each toast carries a unique
 * id so the host can treat a replacement as a fresh notification (resetting its auto-hide timer).
 */
const DEFAULT_SEVERITY = "error";
const GENERIC_MESSAGE = "Something went wrong. Please try again.";

let nextId = 0;
const listeners = new Set();
let bufferedToast = null;

export const subscribe = (fn) => {
    listeners.add(fn);

    if (bufferedToast) {
        const pending = bufferedToast;
        bufferedToast = null;
        fn(pending);
    }

    return () => {
        listeners.delete(fn);
    };
};

export const showToast = (message, severity = DEFAULT_SEVERITY) => {
    const payload = {
        id: ++nextId,
        message: message ? String(message) : GENERIC_MESSAGE,
        severity,
    };

    if (listeners.size > 0) {
        listeners.forEach((fn) => fn(payload));
    } else {
        bufferedToast = payload;
    }
};

export const toast = {
    error: (message) => showToast(message, "error"),
    warning: (message) => showToast(message, "warning"),
    info: (message) => showToast(message, "info"),
    success: (message) => showToast(message, "success"),
};

export default toast;
